import { useEffect, useState, type ReactElement } from "react";

import { Button } from "@/components/ui/button";

const MIN_CLOUD_JOB_BUDGET_CREDITS = 1.38;

export function WorkspaceFundingCard({
  suggestedCredits,
  busy,
  error,
  recovering = false,
  onContinue,
}: {
  suggestedCredits?: number;
  busy: boolean;
  error?: string;
  recovering?: boolean;
  onContinue: (credits: number) => void;
}): ReactElement {
  const [credits, setCredits] = useState(
    String(suggestedCredits ?? 2.04),
  );
  useEffect(() => {
    setCredits(String(suggestedCredits ?? 2.04));
  }, [suggestedCredits]);
  const parsed = Number(credits);
  const valid =
    Number.isFinite(parsed) && parsed >= MIN_CLOUD_JOB_BUDGET_CREDITS;

  return (
    <div className="mt-3 rounded-xl border border-[#d7dfdc] bg-[#f5f8f7] p-3">
      <div className="font-semibold text-[#263e39]">
        {recovering ? "Retry the connection" : "More credits needed"}
      </div>
      <p className="mt-1 text-xs leading-5 text-[#66726d]">
        {recovering
          ? "Your work and credits are safe. Retry the same continuation."
          : "Your work is saved. Continue from the same point with another budget."}
      </p>
      <div className="mt-3 flex flex-wrap items-end gap-2">
        <label className="grid gap-1 text-[11px] font-medium text-[#68716d]">
          Credits for the next step
          <input
            type="number"
            min={MIN_CLOUD_JOB_BUDGET_CREDITS}
            max={100_000}
            step="0.01"
            value={credits}
            disabled={recovering}
            onChange={(event) => setCredits(event.currentTarget.value)}
            className="h-8 w-24 rounded-md border border-[#cbd5d1] bg-white px-2 text-sm text-[#26302d] outline-none focus:border-[#77968e] focus:ring-2 focus:ring-[#dce8e5]"
          />
        </label>
        <Button
          type="button"
          size="sm"
          disabled={busy || !valid}
          onClick={() => onContinue(parsed)}
          className="h-8 bg-[#263e39] hover:bg-[#1b302c]"
        >
          {busy
            ? "Continuing…"
            : recovering
              ? "Retry"
              : "Continue"}
        </Button>
        <a
          href="/billing"
          className="inline-flex h-8 items-center px-2 text-xs font-semibold text-[#476b63] underline-offset-2 hover:underline"
        >
          Add credits
        </a>
      </div>
      {error ? (
        <p className="mt-2 text-xs text-red-700" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
